import styled, { css } from 'styled-components';
import { withRouter } from 'react-router';
import { Route, Link, Switch } from 'react-router-dom';
import { getCrumbName } from 'utils/routes';
import { NavLink, spacing, size, InlineList, media } from 'ui';
import { MdChevronLeft } from 'react-icons/md';
import { MdChevronRight } from 'react-icons/md';
import Helmet from 'react-helmet';
import MobilePlaceSwitcher from 'components/MobilePlaceSwitcher';
import { getAppName } from 'components/AppBanner';
import withSizes from 'react-sizes';
import { useTranslation } from 'react-i18next';
import MobileNotification from './MobileNotification';

export const Title = styled.h1`
  font-size: ${size(1)};
  margin-top: 0;
  margin-bottom: 0;
  display: inline-block;
  font-weight: normal;
  color: #fff;
  font-weight: 500;
  line-height: 24px;

  ${media.tablet`
    line-height: ${spacing(2.25)};
    font-size: ${size(3)};
    color: inherit;
  `};

  &:hover {
    text-decoration: underline;
  }
`;

const BackLink = styled.li`
  color: #fff;
  position: absolute;
  left: ${spacing()};
  top: 50%;
  transform: translateY(-50%);
  font-size: ${size(3)};

  ${media.tablet`display: none;`};

  > a {
    color: inherit;
  }
`;

const HiddenOnMobileListItem = styled.li`
  display: none;
  vertical-align: middle;
  vertical-align: baseline;

  ${media.tablet`
    display: inline-block;
  `};
`;

export const Li = styled.li`
  display: block;
  text-align: center;
  padding-left: ${spacing(2)};
  padding-right: ${spacing(2)};
  line-height: ${spacing(1.5)};

  ${media.tablet`
    display: inline-block;
    text-align: initial;
    padding-left: 0;
    padding-right: 0;
  `};
`;

export const CrumbLink = styled(NavLink)`
  color: ${({ theme }) => theme.text};

  &:hover {
    text-decoration: underline;
  }
`;

export const CrumbLast = styled.span`
  color: #fff;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;

  ${media.tablet`
    color: ${({ theme }) => theme.textLight};
    font-weight: normal;
    display: inline;
  `};
`;

export const Wrapper = styled.div`
  align-items: baseline;
  justify-content: space-between;
  padding: ${spacing()};

  ${media.tablet`
    padding: ${spacing(2)} ${spacing(2)} ${spacing()};
    display: flex;
  `};

  ${props =>
    props.bareHorizontal &&
    css`
      padding-left: 0;
      padding-right: 0;
    `};

  ${props =>
    props.bareVertical &&
    css`
      padding-top: ${spacing(0.5)};
      padding-bottom: ${spacing(0.5)};
    `};

  ${props =>
    props.narrow &&
    css`
      max-width: 800px;
    `};

  ${props =>
    props.shouldBeHiddenOnLargeViewports &&
    css`
      ${media.tablet`
        display: none;
      `};
    `};
`;

export const BreadcrumbContent = styled(InlineList)`
  font-size: ${size(1)};
  display: block;

  ${media.tablet`
    > li + li:before {
      content: '›';
      margin-left: ${spacing(0.5)};
      margin-right: ${spacing(0.5)};
    }
  `};
`;

const BreadcrumbAction = styled.span`
  display: none;
  padding-left: ${spacing()};
  font-size: ${size()};

  ${media.tablet`
    display: inline-block;
  `};
`;

const BreadcrumbSubtitle = styled.span`
  font-size: ${size()};
  padding-top: ${spacing()};
  color: #fff;

  ${media.tablet`
  display: none;
`};
`;

export const BreadcrumbButton = styled(Link)`
  text-decoration: none;
  display: inline-block;
  font-size: ${size(-1)};
  background-color: ${({ theme }) => theme.primary};
  padding: ${spacing(0.25)} ${spacing(0.5)};
  color: #fff;
  border-radius: 999px;
  vertical-align: middle;
`;

const Crumb = withSizes(({ width }) => ({ width }))(props => {
  const {
    location,
    match,
    customTranslations,
    isRoot,
    backLink,
    width,
  } = props;
  const { pathname } = location;
  const { url, params } = match;
  const { crumb } = params;
  const isLastCrumb = pathname === url;
  const { t } = useTranslation('breadcrumbs');

  if (isLastCrumb) {
    return (
      <>
        <Helmet
          title={`${getCrumbName({
            crumb,
            url,
            customTranslations,
            t,
          })} – ${getAppName()}`}
        />

        {backLink && (
          <BackLink>
            <Link to={backLink.url}>
              <MdChevronLeft />
            </Link>
          </BackLink>
        )}

        {!backLink && width <= 799 && (
          <BackLink>
            <MobilePlaceSwitcher />
          </BackLink>
        )}

        {isRoot ? (
          <Li>
            <Title>{getCrumbName({ crumb, url, customTranslations, t })}</Title>
          </Li>
        ) : (
          <Li>
            <CrumbLast>
              {getCrumbName({ crumb, url, customTranslations, t })}
            </CrumbLast>
          </Li>
        )}
      </>
    );
  }

  return (
    <>
      {crumb !== 'payer' && (
        <HiddenOnMobileListItem>
          <CrumbLink to={url}>
            {isRoot ? (
              <Title>
                {getCrumbName({ crumb, url, customTranslations, t })}
              </Title>
            ) : (
              getCrumbName({ crumb, url, customTranslations, t })
            )}
          </CrumbLink>
        </HiddenOnMobileListItem>
      )}

      <Route
        path={`${url}/:crumb`}
        render={() => (
          <ConnectedCrumb
            customTranslations={customTranslations}
            backLink={
              backLink || {
                url,
                label: getCrumbName({ crumb, url, customTranslations, t }),
              }
            }
          />
        )}
      />
    </>
  );
});

const ConnectedCrumb = withRouter(Crumb);

const Breadcrumb = ({
  customTranslations,
  children,
  bareHorizontal,
  narrow,
  backLink,
  shouldBeHiddenOnLargeViewports,
  subtitle,
}) => (
  <Wrapper
    bareHorizontal={bareHorizontal}
    narrow={narrow}
    shouldBeHiddenOnLargeViewports={shouldBeHiddenOnLargeViewports}
    bareVertical={subtitle}
  >
    <BreadcrumbContent>
      <Switch>
        <Route
          path="/:crumb"
          render={() => (
            <ConnectedCrumb
              isRoot
              customTranslations={customTranslations}
              backLink={backLink}
            />
          )}
        />

        <Route
          render={() => (
            <ConnectedCrumb isRoot customTranslations={customTranslations} />
          )}
        />
      </Switch>

      {shouldBeHiddenOnLargeViewports && (
        <li>
          <MobileNotification />
        </li>
      )}

      {subtitle && (
        <Route
          path="/mes-echanges/mes-messages/:id"
          render={({ match }) => (
            <BreadcrumbSubtitle as={NavLink} to={`${match.url}/details`}>
              {subtitle} membres
              <MdChevronRight />
            </BreadcrumbSubtitle>
          )}
        />
      )}
    </BreadcrumbContent>

    {children && <BreadcrumbAction>{children}</BreadcrumbAction>}
  </Wrapper>
);

export default withRouter(Breadcrumb);
